import { toTypedSchema } from "@vee-validate/zod";
import { configureForms } from "@/formsConfig";
import { cloneDeep } from "@/utils/objects";
import { FormContextKey, useForm } from "vee-validate";
import { computed, getCurrentInstance, toValue, type MaybeRefOrGetter } from "vue";
import type { z } from "zod";

// vee-validate is set up here, when the first form loads, and never in an
// entrypoint, so the form library stays out of the first download.
configureForms();

// The context useForm provided. KitForm re-provides it to its fields, so a page
// can own two forms (one per editor) and each KitForm's fields find their own.
export const KIT_FORM_CONTEXT = Symbol("kitFormContext");

// A form a page owns, for a page that needs its state outside the fields: it
// seeds values that arrive after mount with resetForm (which also resets the
// dirty baseline), reads form.values and form.meta.dirty in its own computeds,
// and writes a value no input owns with setFieldValue (a pushed row, a
// regenerate flag). Pass it to <KitForm :form>. The fields stay the only thing
// bound to the values: the page never binds a model into the form.
//
// The schema may be a getter, so a rule built from data that loads later (a
// licence cap, the editor's mode) is the one the form checks against.
export function useKitForm<TValues extends Record<string, unknown>>(
  schema: MaybeRefOrGetter<z.ZodType<TValues, z.ZodTypeDef, unknown>>,
  initialValues?: Partial<TValues>,
) {
  const form = useForm<TValues>({
    validationSchema: computed(() => toTypedSchema(toValue(schema))),
    initialValues: cloneDeep(initialValues) as never,
    // Hiding a field keeps its value. The page's toPayload decides what is sent.
    keepValuesOnUnmount: true,
  });
  // inject() cannot see a component's own provide, so read it off the instance.
  const provides = (getCurrentInstance() as unknown as { provides: Record<symbol, unknown> } | null)?.provides;
  const context = provides?.[FormContextKey as symbol];
  return Object.assign(form, { [KIT_FORM_CONTEXT]: context });
}

export type KitFormContext<TValues extends Record<string, unknown> = Record<string, unknown>> = ReturnType<
  typeof useKitForm<TValues>
>;
